// src/components/StreakBanner.tsx
import React from "react";

function ordinal(n: number): string {
  const remainder100 = n % 100;
  if (remainder100 >= 11 && remainder100 <= 13) return `${n}th`;
  switch (remainder100 % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

function toISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getLast7DaysOrdered(): string[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    return toISODate(d);
  });
}

function getDayLabel(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  return ["S", "M", "T", "W", "T", "F", "S"][new Date(year, month - 1, day).getDay()];
}

interface StreakBannerProps {
  displayName: string;
  photoURL: string | null;
  viewportWidth: number;
  isFirstVisit: boolean;
  currentStreak: number;
  activeDates: string[];
  streakLoading: boolean;
}

const StreakBanner: React.FC<StreakBannerProps> = ({
  displayName,
  photoURL,
  viewportWidth,
  isFirstVisit,
  currentStreak,
  activeDates,
  streakLoading,
}) => {
  const headingSize  = viewportWidth < 360 ? 20 : viewportWidth < 480 ? 24 : viewportWidth < 768 ? 28 : 32;
  const subTextSize  = viewportWidth < 360 ? 13 : viewportWidth < 480 ? 15 : viewportWidth < 768 ? 17 : 20;
  const avatarSize   = viewportWidth < 360 ? 58 : viewportWidth < 480 ? 68 : viewportWidth < 768 ? 78 : 88;
  const circleSize   = viewportWidth < 360 ? 30 : viewportWidth < 480 ? 34 : viewportWidth < 768 ? 38 : 40;
  const circleFontSz = viewportWidth < 360 ? 10 : viewportWidth < 480 ? 11 : 13;

  const name = displayName || "User";

  const greeting = isFirstVisit
    ? `Welcome to BodyOwn, ${name}.`
    : `Welcome back, ${name}.`;

  const streakLabel = `This is your ${ordinal(currentStreak)} streak with us`;

  const window7 = getLast7DaysOrdered();
  const todayISO = toISODate(new Date());
  const activeSet = new Set(activeDates);
  const checkmarkSize = Math.round(circleSize * 0.45);

  // ── LOADING STATE: Branded Spinner inside the Banner area ──────────────────
  if (streakLoading) {
    return (
      <div style={{
        width: "100%",
        height: "160px", // Approximate height of the expanded banner
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'DM Sans', sans-serif",
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid rgba(255, 255, 255, 0.2)',
          borderTop: '4px solid #FFFFFF', // White spinner to contrast with Dashboard background
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          marginBottom: '16px',
          boxSizing: 'border-box'
        }} />
        <p style={{
          color: '#FFFFFF',
          fontSize: '14px',
          fontWeight: '600',
          letterSpacing: '2px',
          textTransform: 'uppercase',
          margin: 0,
          animation: 'pulse 1.5s ease-in-out infinite'
        }}>
          BodyOwn
        </p>
        <style>{`
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        `}</style>
      </div>
    );
  }

  // ── MAIN RENDER ────────────────────────────────────────────────────────────
  return (
    <div style={{
      width: "100%",
      fontFamily: "'DM Sans', sans-serif",
      paddingBottom: 16,
      boxSizing: "border-box",
      display: "block",
    }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        marginBottom: 16,
        gap: 12,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{
            margin: 0,
            fontFamily: "'DM Sans', sans-serif",
            fontSize: headingSize,
            fontWeight: 600,
            lineHeight: 1.3,
            color: "white",
            overflowWrap: "break-word",
            wordBreak: "break-word",
          }}>
            {greeting}
          </h1>
          <p style={{
            margin: "4px 0 0",
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 500,
            fontSize: subTextSize,
            lineHeight: 1.4,
            color: "#FFFFFF",
          }}>
            {streakLabel}
          </p>
        </div>

        <div style={{
          width: avatarSize,
          height: avatarSize,
          minWidth: avatarSize,
          borderRadius: "50%",
          overflow: "hidden",
          flexShrink: 0,
          border: "2.5px solid rgba(255,255,255,0.85)",
          boxShadow: "0 4px 14px rgba(140,100,160,0.35)",
          background: "#E3E3E3",
        }}>
          {photoURL ? (
            <img
              src={photoURL}
              alt="Profile"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          ) : (
            <svg width="100%" height="100%" viewBox="0 0 120 120" preserveAspectRatio="xMidYMid slice">
              <circle cx="60" cy="60" r="60" fill="#E3E3E3" />
              <circle cx="60" cy="49.2" r="20.4" fill="white" />
              <circle cx="60" cy="114" r="38.4" fill="white" />
            </svg>
          )}
        </div>
      </div>

      <div style={{
        paddingTop: 14,
        borderTop: "2px solid rgba(255,255,255,0.55)",
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
      }}>
        {window7.map((iso) => {
          const isActive = activeSet.has(iso);
          return (
            <div key={iso} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
              <div style={{
                width: circleSize,
                height: circleSize,
                minWidth: circleSize,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #B19ABA, #1E5E5B)",
                border: "2px solid rgba(255,255,255,0.85)",
                boxShadow: "0 2px 10px rgba(30,94,91,0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}>
                {isActive && (
                  <svg width={checkmarkSize} height={checkmarkSize} viewBox="0 0 18 18" fill="none">
                    <path d="M3.5 9.5L7 13L14.5 5" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <span style={{
                fontSize: circleFontSz,
                fontWeight: iso === todayISO ? 900 : 700,
                fontFamily: "'DM Sans', sans-serif",
                color: "white",
                lineHeight: 1,
              }}>
                {getDayLabel(iso)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StreakBanner;